import { useState, useEffect } from 'react';
import { useTimerContext } from '@/contexts/TimerContext';
import { TimeEntryModal } from '@/components/matters/TimeEntryModal';
import { useConfirm } from '@/hooks/useConfirm';

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${hours}:${pad(minutes)}:${pad(seconds)}`;
}

export function TimerBar() {
  const { timer, isRunning, isPaused, stopTimer, discardTimer } = useTimerContext();
  const [showModal, setShowModal] = useState(false);
  const [stoppedData, setStoppedData] = useState<{
    durationMinutes: number;
    matterId: string;
    clientId: string;
    referenceId?: string;
    referenceType?: string;
    referenceLabel?: string;
    matterTitle: string;
  } | null>(null);
  const [displayTime, setDisplayTime] = useState('0:00:00');
  const confirm = useConfirm();

  // Tick every second
  useEffect(() => {
    if (!timer) return;
    function tick() {
      if (!timer) return;
      const now = Date.now();
      let totalMs: number;
      if (timer.paused) {
        totalMs = timer.accumulatedMs;
      } else {
        totalMs = now - timer.startedAt;
      }
      setDisplayTime(formatElapsed(totalMs));
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [timer]);

  if (!timer && !showModal) return null;

  function handleStop() {
    const result = stopTimer();
    if (result) {
      setStoppedData({
        durationMinutes: result.durationMinutes,
        matterId: result.state.matterId,
        clientId: result.state.clientId,
        referenceId: result.state.referenceId,
        referenceType: result.state.referenceType,
        referenceLabel: result.state.referenceLabel,
        matterTitle: result.state.matterTitle,
      });
      setShowModal(true);
    }
  }

  async function handleDiscard() {
    const confirmed = await confirm({
      title: 'Discard Time Recording',
      message: 'Discard this time recording?',
      variant: 'danger'
    });
    if (confirmed) {
      discardTimer();
    }
  }

  return (
    <>
      {timer && (
        <div className="flex items-center justify-between bg-brand-600 px-4 py-2 text-white dark:bg-brand-800">
          <div className="flex items-center gap-3">
            <div className="relative flex h-2.5 w-2.5">
              {isRunning && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
              )}
              <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${isRunning ? 'bg-red-500' : 'bg-yellow-400'}`} />
            </div>
            <span className="text-sm font-medium">
              {isPaused ? 'Paused' : 'Recording'}
            </span>
            <span className="font-mono text-sm font-bold">{displayTime}</span>
            <span className="text-sm text-brand-200">
              {timer.matterTitle}
              {timer.referenceLabel && ` / ${timer.referenceLabel}`}
            </span>
            {isPaused && (
              <span className="rounded bg-yellow-500/20 px-2 py-0.5 text-xs text-yellow-200">
                Idle - move mouse to resume
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDiscard}
              className="rounded px-3 py-1 text-sm text-brand-200 hover:bg-brand-700 hover:text-white"
            >
              Discard
            </button>
            <button
              onClick={handleStop}
              className="rounded bg-white px-3 py-1 text-sm font-medium text-brand-700 hover:bg-brand-50"
            >
              Stop & Log
            </button>
          </div>
        </div>
      )}

      {showModal && stoppedData && (
        <TimeEntryModal
          matterId={stoppedData.matterId}
          clientId={stoppedData.clientId}
          referenceId={stoppedData.referenceId}
          referenceType={stoppedData.referenceType as any}
          referenceLabel={stoppedData.referenceLabel}
          prefillDurationMinutes={stoppedData.durationMinutes}
          onClose={() => {
            setShowModal(false);
            setStoppedData(null);
          }}
        />
      )}
    </>
  );
}
